/**
 * The built-in relationship dimension shared by companies and contacts.
 *
 * Both entities store a MULTI-value relationship array
 * (`organization_relationship` / `contact_relationship`), and both list pages
 * render it as badges and filter it as a multiselect. Keeping the value list,
 * the badge variant and the option builder here is what stops the two sibling
 * pages from drifting apart — the map used to live inside `ContactList` only,
 * so the companies list had no colouring to be consistent with.
 *
 * The value set itself is single-sourced from
 * `shared/config/business_classification.config.json` — never re-typed. It is
 * read here through the GENERATED bindings rather than the raw config JSON, so
 * the value set and its `user_assignable` axis arrive together and a config edit
 * can't reach one and miss the other.
 *
 * Two value sets, and the difference is load-bearing:
 *   - `RELATIONSHIP_TYPE_VALUES` (generated) is the STORED vocabulary — what a
 *     row may hold and what the create/update schemas accept. It includes
 *     `bank`, which `bank_account_service` stamps on a company so the
 *     bank-account flow can find it again.
 *   - `ASSIGNABLE_RELATIONSHIP_VALUES` (below) is what a HUMAN may pick. `bank`
 *     is system-assigned and deliberately absent: no surface asks a user to tag
 *     a company that way, and it is meaningless on a contact, which shares this
 *     vocabulary. Every picker, filter and bulk write reads this one.
 *
 * Not covered here: tenant-defined relationship CLASSES
 * (`custom_relationship_classes`), which are a separate table-backed dimension
 * with its own membership joins, not members of these arrays.
 */
import type { AssignableRelationshipType } from '../../../../generated/api/models/assignableRelationshipType';
import {
  RELATIONSHIP_TYPE_VALUES,
  relationshipTypeUserAssignable,
  type RelationshipType,
} from '../../../../generated/config-enums';
import classificationConfig from '../../../../shared-config/business_classification.config.json';

/**
 * The built-in relationship values a user may pick, in config order.
 *
 * NOT every stored value — see the module doc above. Derived from the config's
 * `user_assignable` flag, never hand-listed.
 *
 * Typed as `AssignableRelationshipType` — the generated type behind
 * `shared/schemas/enums/assignable_relationship_type.schema.json`, which the two
 * bulk-relationship request schemas `$ref`. Both sides come off the same config
 * flag, so the annotation is also the compile-time check that they agree: a
 * picker offering a value the endpoint no longer accepts stops building.
 */
export const ASSIGNABLE_RELATIONSHIP_VALUES: AssignableRelationshipType[] =
  RELATIONSHIP_TYPE_VALUES.filter(
    (value): value is AssignableRelationshipType => relationshipTypeUserAssignable[value],
  );

/**
 * The complement: values a row may HOLD that no human may assign.
 *
 * `bank` (stamped by accounting when a bank account names a company) and
 * `employee` (stamped by people's `EmployeeService` on the contact backing an
 * employee) today, derived from the same flag so a third one inherits the
 * behaviour with no edit here.
 *
 * A surface that OFFERS a choice reads `ASSIGNABLE_RELATIONSHIP_VALUES`; a
 * surface that RENDERS what a record holds has to account for these too, or it
 * shows the record as having no relationship at all while the list beside it
 * badges one (WI 21fb0a22).
 */
export const SYSTEM_ASSIGNED_RELATIONSHIP_VALUES: readonly RelationshipType[] =
  RELATIONSHIP_TYPE_VALUES.filter((value) => !relationshipTypeUserAssignable[value]);

/**
 * Widened for LOOKUP only.
 *
 * The exported array stays `RelationshipType[]` so it is exhaustiveness-checked
 * against the config like its assignable sibling; the predicate below is asked
 * about values that arrive as a bare `string` (a stored array off an API
 * payload), which that narrow element type would reject at the call site rather
 * than answer.
 */
const SYSTEM_ASSIGNED_LOOKUP = new Set<string>(SYSTEM_ASSIGNED_RELATIONSHIP_VALUES);

/** Whether a stored value is one the system assigns and the user may not touch. */
export const isSystemAssignedRelationship = (relationship: string): boolean =>
  SYSTEM_ASSIGNED_LOOKUP.has(relationship);

/**
 * The rungs of the sales ladder, which the DEAL LIFECYCLE owns.
 *
 * These three values are written by deal events (created → prospect, won →
 * customer, lost → back to lead), never by an edit form. The update endpoints
 * persist the payload's other tags unioned with the rungs already stored, so a
 * rung submitted from a form is ignored — a form whose page loaded before a deal
 * was won would otherwise revert it (see
 * `modules/master_data/contacts/relationship_ownership.py`).
 *
 * The forms read this to render those toggles as read-only rather than letting a
 * user pick a value the save silently drops.
 *
 * Derived from the same config as the value set — `salesLadderPosition` is the
 * config's own statement of which values the ladder owns — so the backend's
 * `SALES_RELATIONSHIP_ORDER` and this list cannot drift apart.
 *
 * This is a THIRD axis, independent of `user_assignable`: a rung is pickable in
 * the sense that it renders in the forms, it is just rendered read-only there
 * because the lifecycle writes it. Read the raw config here rather than a
 * generated binding — `salesLadderPosition` is `number | null`, which the
 * generator's `extra_maps` cannot emit (it requires one type across all entries).
 */
export const LIFECYCLE_OWNED_RELATIONSHIPS: readonly string[] =
  Object.entries(classificationConfig.relationship_types)
    // `filter` already copies, so the `sort` below cannot reorder the imported
    // config object's own entries, which every other reader shares.
    .filter(([, meta]) => meta.salesLadderPosition !== null)
    .sort(([, a], [, b]) => (a.salesLadderPosition ?? 0) - (b.salesLadderPosition ?? 0))
    .map(([value]) => value);

/** Whether the deal lifecycle, not the edit form, decides this value. */
export const isLifecycleOwnedRelationship = (relationship: string): boolean =>
  LIFECYCLE_OWNED_RELATIONSHIPS.includes(relationship);

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const RELATIONSHIP_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  lead: 'outline',
  prospect: 'outline',
  customer: 'default',
  vendor: 'secondary',
  partner: 'default',
  competitor: 'destructive',
  recruitment: 'secondary',
};

/** Badge variant for a relationship value; anything unmapped reads as neutral. */
export const getRelationshipBadgeVariant = (relationship: string): BadgeVariant =>
  RELATIONSHIP_BADGE_VARIANTS[relationship] || 'secondary';

/**
 * Filter/display options for one entity's relationship.
 *
 * `scope` picks the i18n namespace, because the two entities label the same
 * value differently — `recruitment` is "Recruitment Agency" for a company and
 * "Recruiter" for a contact.
 */
export const buildRelationshipOptions = (
  t: (key: string) => string,
  scope: 'companyRelationship' | 'contactRelationship',
): { value: AssignableRelationshipType; label: string }[] =>
  ASSIGNABLE_RELATIONSHIP_VALUES.map((value) => ({
    value,
    label: t(`common:businessClassification.${scope}.${value}`),
  }));

/**
 * The two sections of the ONE relationship filter on the list pages.
 *
 * The built-in vocabulary and the tenant's own relationship CLASSES used to be a
 * filter and — nothing: there was no class filter at all, so a tenant could tag a
 * company "Interior Designer" and then had no way to list its interior designers
 * (WI ca0962b0). Merging them into one multiselect is also what makes the two
 * OR'd: separate filter controls AND, and "Customer AND Interior Designer" is
 * the opposite of what one list of checkboxes means.
 */
export const RELATIONSHIP_FILTER_GROUP_BUILT_IN = 'builtIn';
export const RELATIONSHIP_FILTER_GROUP_CLASS = 'customRelationship';

/**
 * Split one picker's selection back into the two request params it feeds.
 *
 * Keyed on the BUILT-IN value set, never on the loaded class list: that set is a
 * closed, generated constant available synchronously, so a deep link
 * (`?relationship=customer,<uuid>`) splits correctly on the very first render —
 * whereas splitting on the classes would mis-file every class id as a
 * relationship value for as long as that query was in flight, and the list would
 * flash a wrongly-empty table.
 *
 * A value that is neither a built-in nor UUID-SHAPED stays in the relationship
 * arm, where the server has always been lenient and an unrecognised value simply
 * matches nothing. Routing it to `class_ids` instead would 422 the whole list
 * request — that param is typed `list[UUID]` — so one junk character in a
 * hand-edited URL would turn a narrowed-to-zero table into a broken page.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const splitRelationshipFilterValues = (
  values: readonly string[] | undefined,
): { relationship: string[]; classIds: string[] } => {
  const builtIn = new Set<string>(ASSIGNABLE_RELATIONSHIP_VALUES);
  const relationship: string[] = [];
  const classIds: string[] = [];
  for (const value of values ?? []) {
    if (!builtIn.has(value) && UUID_PATTERN.test(value)) {
      classIds.push(value);
    } else {
      relationship.push(value);
    }
  }
  return { relationship, classIds };
};

/**
 * The filter's flat option list, each option tagged with the section it renders
 * in. Flat because everything downstream — `multiSelectParam`'s "empty AND full
 * both mean all" rule, the active-filter chip, the trigger's label — reads one
 * list, and a second source is how a chip comes to name a value the request
 * never carried.
 */
export const buildRelationshipFilterOptions = (
  builtInOptions: readonly { value: string; label: string }[],
  classOptions: readonly { value: string; label: string }[],
): { value: string; label: string; group: string }[] => [
  ...builtInOptions.map((option) => ({
    ...option,
    group: RELATIONSHIP_FILTER_GROUP_BUILT_IN,
  })),
  ...classOptions.map((option) => ({
    ...option,
    group: RELATIONSHIP_FILTER_GROUP_CLASS,
  })),
];
